/**
 * Soft goalmouth frame used by match-report on-target checks and Insights GK zones.
 * Coordinates are normalized 0-1 on goal-scored-1500x583.png (Y grows downward).
 */

export const GOAL_FRAME = {
    /** Top of the mouth: anything above this (smaller Y) is over the crossbar. */
    crossbar: 0.346,
    leftPost: 0.167,
    rightPost: 0.838,
    /** Bottom of the usable mouth in image space. */
    bottom: 1.0,
} as const;

export type ZoneId = 'TL' | 'TC' | 'TR' | 'ML' | 'MC' | 'MR' | 'BL' | 'BC' | 'BR';

export const ZONE_IDS: ZoneId[] = ['TL', 'TC', 'TR', 'ML', 'MC', 'MR', 'BL', 'BC', 'BR'];

export const ZONE_LABELS: Record<ZoneId, string> = {
    TL: 'Top left',
    TC: 'Top centre',
    TR: 'Top right',
    ML: 'Mid left',
    MC: 'Mid centre',
    MR: 'Mid right',
    BL: 'Bottom left',
    BC: 'Bottom centre',
    BR: 'Bottom right',
};

const usable = (x?: number | null, y?: number | null): boolean =>
    x != null && y != null && x >= 0 && x <= 1 && y >= 0 && y <= 1;

/** Same inequalities as the historical isShotOnTarget soft box. */
export const isInsideGoalMouth = (x?: number | null, y?: number | null): boolean => {
    if (!usable(x, y)) return false;
    return (
        (y as number) > GOAL_FRAME.crossbar &&
        (x as number) > GOAL_FRAME.leftPost &&
        (x as number) < GOAL_FRAME.rightPost
    );
};

/**
 * Off-mouth click that still sits on the frame (posts / crossbar).
 * ponytail: no stored post flag - band is ~4% of the image; tighten if we log post as an enum.
 */
export const POST_BAND = 0.04;

export const isOnPost = (x?: number | null, y?: number | null): boolean => {
    if (!usable(x, y) || isInsideGoalMouth(x, y)) return false;
    const px = x as number;
    const py = y as number;
    const { leftPost, rightPost, crossbar, bottom } = GOAL_FRAME;
    const nearLeft =
        px <= leftPost &&
        px >= leftPost - POST_BAND &&
        py >= crossbar - POST_BAND &&
        py <= bottom;
    const nearRight =
        px >= rightPost &&
        px <= rightPost + POST_BAND &&
        py >= crossbar - POST_BAND &&
        py <= bottom;
    const nearBar =
        py <= crossbar &&
        py >= crossbar - POST_BAND &&
        px >= leftPost - POST_BAND &&
        px <= rightPost + POST_BAND;
    return nearLeft || nearRight || nearBar;
};

/**
 * 3×3 bins inside the goalmouth. Image Y grows down, so Top row is nearest the crossbar.
 * Returns null when coords are missing; 'offTarget' when present but outside the mouth.
 */
export const zoneIdForTarget = (
    x?: number | null,
    y?: number | null
): ZoneId | 'offTarget' | null => {
    if (!usable(x, y)) return null;
    if (!isInsideGoalMouth(x, y)) return 'offTarget';

    const px = x as number;
    const py = y as number;
    const { leftPost, rightPost, crossbar, bottom } = GOAL_FRAME;
    const mouthW = rightPost - leftPost;
    const mouthH = bottom - crossbar;
    const col = Math.min(2, Math.floor(((px - leftPost) / mouthW) * 3));
    const row = Math.min(2, Math.floor(((py - crossbar) / mouthH) * 3));
    const cols: Array<'L' | 'C' | 'R'> = ['L', 'C', 'R'];
    const rows: Array<'T' | 'M' | 'B'> = ['T', 'M', 'B'];
    return `${rows[row]}${cols[col]}` as ZoneId;
};

/** Absolute % box for overlaying a zone on the goal image. */
export const zoneRectPercent = (
    zone: ZoneId
): { left: number; top: number; width: number; height: number } => {
    const { leftPost, rightPost, crossbar, bottom } = GOAL_FRAME;
    const mouthW = rightPost - leftPost;
    const mouthH = bottom - crossbar;
    const col = zone.endsWith('L') ? 0 : zone.endsWith('C') ? 1 : 2;
    const row = zone.startsWith('T') ? 0 : zone.startsWith('M') ? 1 : 2;
    return {
        left: (leftPost + (col * mouthW) / 3) * 100,
        top: (crossbar + (row * mouthH) / 3) * 100,
        width: (mouthW / 3) * 100,
        height: (mouthH / 3) * 100,
    };
};
